import { useEffect, useState } from 'react';
import { Tag as TagIcon } from 'lucide-react';
import type { TagRef } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TaxonomyPicker } from './taxonomy-picker';
import { useBulkTagPosts } from '@/lib/posts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postIds: string[];
  /** Whether the current user can create taxonomy entries (gates the
   *  "+ สร้างใหม่" affordance inside the inline picker). */
  canCreate: boolean;
  /** Fired after a successful mutation so the parent can clear its
   *  bulk selection. */
  onApplied?: () => void;
}

/**
 * Dialog for adding/removing tags across multiple selected posts in
 * one request. Two TaxonomyPickers — one for additions, one for
 * removals — independent state, both validated client-side before
 * submit (at least one must be non-empty, otherwise button stays
 * disabled).
 *
 * Resets state on close so reopening is a clean slate (avoids stale
 * additions accidentally being re-applied to a different selection).
 */
export function BulkTagDialog({ open, onOpenChange, postIds, canCreate, onApplied }: Props) {
  const [adds, setAdds] = useState<TagRef[]>([]);
  const [removes, setRemoves] = useState<TagRef[]>([]);
  const mutation = useBulkTagPosts();

  useEffect(() => {
    if (open) {
      setAdds([]);
      setRemoves([]);
    }
  }, [open]);

  const canSubmit = adds.length + removes.length > 0 && !mutation.isPending;

  const submit = () => {
    if (!canSubmit) return;
    mutation.mutate(
      {
        ids: postIds,
        addTagIds: adds.length > 0 ? adds.map((t) => t.id) : undefined,
        removeTagIds: removes.length > 0 ? removes.map((t) => t.id) : undefined,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onApplied?.();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TagIcon className="h-5 w-5 text-slate-400" />
            แก้แท็ก {postIds.length} บทความพร้อมกัน
          </DialogTitle>
          <DialogDescription>
            เพิ่มหรือลบแท็ก ไม่กระทบแท็กอื่นที่อยู่บนบทความ — แท็กที่มีอยู่แล้วจะข้าม
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <TaxonomyPicker
            kind="tag"
            label="เพิ่มแท็ก"
            selected={adds}
            onChange={(next) => setAdds(next)}
            canCreate={canCreate}
            placeholder="เลือกหรือสร้างแท็กที่จะเพิ่ม"
          />
          <TaxonomyPicker
            kind="tag"
            label="ลบแท็ก"
            selected={removes}
            onChange={(next) => setRemoves(next)}
            canCreate={false}
            placeholder="เลือกแท็กที่จะลบ"
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            ยกเลิก
          </Button>
          <Button type="button" onClick={submit} disabled={!canSubmit}>
            {mutation.isPending ? 'กำลังอัปเดต...' : 'อัปเดต'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
